import React, { useCallback, useEffect, useRef, useState, } from 'react';
import ReactFlow, { addEdge, Background, ReactFlowProvider, MiniMap, useNodesState, useEdgesState, Controls, } from 'react-flow-renderer';
import { useSelector } from 'react-redux';
import { styled } from 'styled-components';
import { Site, TimespanUnit } from '@debbie/common/dist/index.js';
import { getPastUpdate, PathType, TimingType, } from '@debbie/common/dist/workflows/index.js';
import { c } from '@debbie/cortex/dist/index.js';
import * as workflowsLib from '@debbie/cortex/dist/api/workflows.js';
import { selectUpdateTypesMap } from '@debbie/cortex/dist/update-types/selectors.js';
import THEME from '../../theme.js';
import Button from '../Button.js';
import { Save, ThumbsUp } from '../Icons/index.js';
import Plus from '../Icons/Plus.js';
import Loading from '../Loading.js';
import AddUpdate from './AddUpdate.js';
import CustomEdge from './CustomEdge.js';
import EdgeEditor from './EdgeEditor.js';
import helpers, { NodeTypes } from './helpers.js';
import NodeEditor from './NodeEditor.js';
import NodeEditorWithCaseContext from './NodeEditorWithCaseContext.js';
import WorkflowInfo from './WorkflowInfo.js';
import { selectMe } from '@debbie/cortex/dist/me/selectors.js';
const edgeTypes = {
    custom: CustomEdge,
};
const _Workflow = (props) => {
    const reactFlowWrapper = useRef(null);
    const workflow = props.workflow;
    const site = useSelector((s) => s.site);
    const updateTypesMap = useSelector(selectUpdateTypesMap);
    const me = useSelector(selectMe);
    const [nodes, setNodes, onNodesChange] = useNodesState([]);
    const [edges, setEdges, onEdgesChange] = useEdgesState([]);
    const [saving, setSaving] = useState(false);
    const [doneSaving, setDoneSaving] = useState(false);
    const [showUpdatesModal, setShowUpdatesModal] = useState(false);
    const [reactFlowInstance, setReactFlowInstance] = useState(null);
    const onDeleteNode = (taskId) => {
        setNodes((nodes) => nodes.filter((node) => node.id !== taskId));
        setEdges((edges) => edges.filter((edge) => edge.source !== taskId && edge.target !== taskId));
    };
    const onDeleteEdge = (edgeId) => {
        setEdges((edges) => edges.filter((edge) => edge.id !== edgeId));
    };
    const setNodesAndEdges = () => {
        // Nodes
        setNodes([helpers.getStartingPointNode()].concat(helpers.getNodesFromWorkflow(workflow, updateTypesMap, editable ? onDeleteNode : undefined, props.caseContext, workflow.workflowId)));
        // Edges
        setEdges(helpers.getEdgesFromWorkflow(workflow, updateTypesMap, props.caseContext));
    };
    useEffect(() => {
        setNodesAndEdges();
    }, [props.workflow]);
    useEffect(() => {
        if (props.caseContext && reactFlowInstance) {
            const pastUpdate = props.caseContext &&
                getPastUpdate(props.caseContext.updates, props.workflow.workflowId);
            const pastTask = pastUpdate &&
                props.workflow.tasks.find((task) => task.id === pastUpdate.taskId);
            if (pastTask) {
                reactFlowInstance &&
                    // @ts-ignore
                    reactFlowInstance.setCenter(pastTask.position.x + 160, pastTask.position.y + 160, {
                        zoom: 0.8,
                        duration: 500,
                    });
            }
        }
    }, [reactFlowInstance, props.workflow]);
    const onDragOver = useCallback((event) => {
        event.preventDefault();
        event.dataTransfer.dropEffect = 'move';
    }, []);
    const onDrop = useCallback((event) => {
        event.preventDefault();
        //@ts-ignore
        const reactFlowBounds = reactFlowWrapper.current?.getBoundingClientRect();
        const data = event.dataTransfer.getData('application/reactflow');
        const updateType = data && JSON.parse(data);
        // check if the dropped element is valid
        if (!(updateType && updateType.updateTypeId)) {
            return;
        }
        //@ts-ignore
        const position = reactFlowInstance.project({
            x: event.clientX - reactFlowBounds.left - 125,
            y: event.clientY - reactFlowBounds.top - 85,
        });
        setNodes((nds) => nds.concat(helpers.fromTaskToNode({
            id: crypto.randomUUID(),
            updateTypeId: updateType.updateTypeId,
            position,
            continueAtTiming: null,
            remindNow: null,
        }, [], updateTypesMap, onDeleteNode, undefined, workflow.workflowId)));
    }, [updateTypesMap, reactFlowInstance]);
    const onConnect = useCallback((params) => {
        const largestPriorityEdge = edges
            .filter((edge) => edge.source === params.source)
            .sort((a, b) => b.data.priority - a.data.priority)[0];
        const priority = largestPriorityEdge?.data?.priority
            ? largestPriorityEdge?.data?.priority + 1
            : 1;
        const targetNode = nodes.find((node) => node.id === params.target);
        const targetUpdateType = updateTypesMap[targetNode?.data.updateTypeId];
        const defaultRequirements = helpers.getDefaultRequirements(targetUpdateType);
        setEdges((eds) => addEdge(helpers.getEdgeFromPath({
            ...params,
            target: params.target,
            source: params.source,
            id: crypto.randomUUID(),
            type: PathType.Normal,
            manual: false,
            priority: priority,
            caseContext: props.caseContext,
            timing: params.source === NodeTypes.StartingPoint
                ? {
                    type: TimingType.Timespan,
                    details: {
                        timespan: [0, TimespanUnit.Days],
                    },
                }
                : {
                    type: TimingType.Timespan,
                    details: {
                        timespan: [12, TimespanUnit.Days],
                    },
                },
            requirements: defaultRequirements,
        }, workflow.tasks, updateTypesMap, props.caseContext), eds));
    }, [edges, nodes]);
    const selectedEdge = edges.find((edge) => edge.selected) || null;
    const selectedEdgeTargetTask = (selectedEdge && nodes.find((node) => node.id === selectedEdge.target)) ||
        null;
    const selectedNode = nodes.find((node) => node.selected) || null;
    const editable = (!workflow.public || !!me?.details.editPublicContent) &&
        site === Site.Caseworker;
    return (React.createElement("div", { style: {
            height: props.height || `calc(100vh - ${THEME.NAV_TOP_HEIGHT}px)`,
            position: 'relative',
            userSelect: 'none',
        } },
        !props.caseContext && (React.createElement(Header, null,
            React.createElement(WorkflowInfo, { workflow: workflow, editable: editable }),
            React.createElement(ButtonContainer, null,
                editable &&
                    (doneSaving ? (React.createElement(ThumbsUp, { style: {
                            height: '1.5em',
                            width: '1.5em',
                            margin: '-0.5em 1.3em -0.5em 0em',
                            cursor: 'pointer',
                        } })) : saving ? (React.createElement(Loading, { style: {
                            height: '1.5em',
                            width: '1.5em',
                            margin: '-0.5em 1.45em -0.5em 0em',
                        } })) : (React.createElement(Save, { onClick: async () => {
                            setSaving(true);
                            try {
                                await workflowsLib.update(workflow.workflowId, {
                                    paths: helpers.getPathsFromEdges(edges, nodes),
                                    tasks: helpers.getTasksFromNodes(nodes, edges),
                                });
                                await c.workflows.getAll();
                            }
                            catch (error) {
                                console.error(error);
                                alert('Error');
                            }
                            setSaving(false);
                            setDoneSaving(true);
                            setTimeout(() => setDoneSaving(false), 2000);
                        }, style: {
                            height: '1.5em',
                            width: '1.5em',
                            margin: '-0.5em 1.3em -0.5em 0em',
                            cursor: 'pointer',
                        } }))),
                editable && (React.createElement(Button, { size: "small", onClick: () => setShowUpdatesModal(!showUpdatesModal) },
                    React.createElement(Plus, { style: {
                            width: '18px',
                            height: '18px',
                            margin: '-0.3em 0',
                        } })))))),
        editable && showUpdatesModal && !props.caseContext && (React.createElement(AddUpdate, { workflowId: workflow.workflowId })),
        selectedEdge && !props.caseContext && Site.Caseworker === site && (React.createElement(EdgeEditor, { edge: selectedEdge, onDelete: () => onDeleteEdge(selectedEdge.id), editable: editable, warnings: selectedEdgeTargetTask?.data.updateTypeId &&
                helpers.getPathWarnings(updateTypesMap[selectedEdgeTargetTask.data.updateTypeId], selectedEdge.data.requirements), onChange: (edgeId, change) => {
                setEdges((edges) => edges.map((edge) => {
                    if (edge.id == edgeId) {
                        return {
                            ...edge,
                            data: {
                                ...edge.data,
                                ...change,
                            },
                        };
                    }
                    return edge;
                }));
            } })),
        selectedNode && !props.caseContext && Site.Caseworker === site && (React.createElement(NodeEditor, { node: selectedNode, workflowId: workflow.workflowId, editable: editable, onChange: (nodeId, change) => {
                setNodes((nodes) => nodes.map((node) => {
                    if (node.id == nodeId) {
                        return {
                            ...node,
                            data: {
                                ...node.data,
                                ...change,
                            },
                        };
                    }
                    return node;
                }));
            } })),
        selectedNode &&
            props.caseContext &&
            props.closeModal &&
            Site.Caseworker === site && (React.createElement(NodeEditorWithCaseContext, { node: selectedNode, caseContext: props.caseContext, workflow: workflow, closeModal: props.closeModal })),
        React.createElement(ReactFlowProvider, null,
            React.createElement(FlowWrapper, { ref: reactFlowWrapper },
                // @ts-expect-error
                React.createElement(ReactFlow, { style: {
                        height: props.height || `calc(100vh - ${THEME.NAV_TOP_HEIGHT}px)`,
                    }, 
                    // @ts-ignore
                    onInit: setReactFlowInstance, onDrop: onDrop, onDragOver: onDragOver, nodes: nodes, edges: edges, onNodesChange: props.caseContext
                        ? // @ts-expect-error
                            (changes) => {
                                for (let i = 0; i < changes.length; i++) {
                                    const change = changes[i];
                                    if (change.type === 'select')
                                        onNodesChange([change]);
                                }
                            }
                        : onNodesChange, onEdgesChange: onEdgesChange, onConnect: onConnect, edgeTypes: edgeTypes, onSelect: console.log, snapToGrid: true }),
                React.createElement(Background, { color: '#000000', gap: 16 }),
                React.createElement(MiniMap, null),
                React.createElement(Controls, null)))));
};
export default _Workflow;
const FlowWrapper = styled.div `
  position: relative;

  .react-flow__node-default,
  .react-flow__node-input {
    padding: 16px;
    width: 250px;
    border: none;
    box-shadow: 1px 1px 10px 0px #ccc !important;
    z-index: -1 !important;

    &.starting-point {
      width: 56px;
      border-radius: 30px;
    }

    &.selected,
    &.selected:hover {
      border: 2px solid ${THEME.COLOR_ACCENT};
    }

    .react-flow__handle {
      background: ${THEME.COLOR_SECONDARY};
      border: 2px solid ${THEME.COLOR_ACCENT};
      height: 10px;
      width: 10px;

      &.react-flow__handle-top {
        top: -8px;
      }
      &.react-flow__handle-bottom {
        bottom: -8px;
      }
    }
  }

  path.react-flow__edge-path {
    stroke-width: 1.5;
  }

  .react-flow__edge.selected path.react-flow__edge-path {
    stroke: ${THEME.COLOR_ACCENT};
  }
`;
const Header = styled.div `
  position: absolute;
  display: flex;
  justify-content: space-between;
  width: 100%;
  padding: ${THEME.GRID_SPACING}px ${THEME.GRID_SPACING}px 0
    ${THEME.GRID_SPACING}px;
  z-index: 9;
  height: 0px;

  h3 {
    margin: 0;
    font-size: 1.5em;

    svg {
      margin: 0.12em 0 -0.12em;
    }
  }
`;
const ButtonContainer = styled.div `
  text-align: right;

  button:not(last-child) {
    margin-right: ${THEME.GRID_SPACING}px;
  }
`;
//# sourceMappingURL=Workflow.js.map